import React, { createContext, useContext, useEffect, useState } from "react";
import { COMPACT_QUERY, useIsMobile } from "../hooks/useMediaQuery";

const STORAGE_KEY = "super.sidebarCollapsed";

const readStoredCollapsed = () => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === null ? null : value === "true";
  } catch {
    return null;
  }
};

const storeCollapsed = (value) => {
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // Storage blocked (private mode etc.) - the preference just isn't kept.
  }
};

const LayoutContext = createContext({
  isMobile: false,
  collapsed: false,
  mobileNavOpen: false,
  toggleSidebar: () => {},
  closeMobileNav: () => {},
});

export const LayoutContextProvider = ({ children }) => {
  const isMobile = useIsMobile();
  // Desktop remembers the admin's choice; with no stored choice, tablets and
  // small laptops start on the icon rail to leave room for tables.
  const [collapsed, setCollapsed] = useState(() => {
    const stored = readStoredCollapsed();
    if (stored !== null) return stored;
    return window.matchMedia ? window.matchMedia(COMPACT_QUERY).matches : false;
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    if (!isMobile) setMobileNavOpen(false);
  }, [isMobile]);

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileNavOpen((open) => !open);
      return;
    }
    setCollapsed((value) => {
      storeCollapsed(!value);
      return !value;
    });
  };

  const closeMobileNav = () => setMobileNavOpen(false);

  return (
    <LayoutContext.Provider
      value={{ isMobile, collapsed, mobileNavOpen, toggleSidebar, closeMobileNav }}
    >
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = () => useContext(LayoutContext);
